import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { api, fmt } from "../lib/api";
import { Spade, Dices, Trophy, Wallet, Zap } from "lucide-react";
import DailyBonusCard from "../components/DailyBonusCard";

const BJ_IMG = "https://images.pexels.com/photos/3678384/pexels-photo-3678384.jpeg";
const RL_IMG = "https://images.pexels.com/photos/7594180/pexels-photo-7594180.jpeg";
const SL_IMG = "https://images.pexels.com/photos/20843727/pexels-photo-20843727.jpeg";

export default function Lobby() {
  const { user, lang, currency } = useApp();
  const [jackpot, setJackpot] = useState({ HUF: 0, EUR: 0 });
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/games/slots/jackpot").then(r=>setJackpot(r.data)).catch(()=>{});
    api.get("/history").then(r=>setHistory(r.data)).catch(()=>{});
  }, []);

  const games = [
    { to:"/blackjack", img: BJ_IMG, name: t(lang,"bj.title"), icon:<Spade size={20}/>, desc:"Classic 21" },
    { to:"/roulette", img: RL_IMG, name: t(lang,"roulette.title"), icon:<Dices size={20}/>, desc:"European 0-36" },
    { to:"/slots", img: SL_IMG, name: t(lang,"slots.title"), icon:<Trophy size={20}/>, desc:"Triple reels · Jackpot" },
  ];

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-6">
        <DailyBonusCard/>
      </div>
      <div className="grid lg:grid-cols-3 gap-6 mb-12">
        <div className="lux-card p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.4em] text-[#d4af37] mb-2">{t(lang,"lobby.welcome")}</div>
          <h1 data-testid="lobby-welcome" className="font-serif text-3xl">{user?.name}</h1>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-md bg-black/40 border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Gold Coins · GC</div>
              <div data-testid="balance-huf" className="font-serif text-2xl text-[#d4af37] mt-1">{fmt(user?.balance_huf, "HUF")}</div>
            </div>
            <div className="rounded-md bg-black/40 border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Premium Coins · PC</div>
              <div data-testid="balance-eur" className="font-serif text-2xl text-[#d4af37] mt-1">{fmt(user?.balance_eur, "EUR")}</div>
            </div>
          </div>
          <Link to="/cashier" data-testid="cta-deposit" className="btn-burgundy mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm uppercase tracking-widest"><Wallet size={16}/>{t(lang,"cashier.deposit")}</Link>
        </div>
        <div className="lux-card p-6 jackpot-glow text-center flex flex-col justify-center">
          <div className="text-xs uppercase tracking-[0.4em] text-[#d4af37] mb-2">JACKPOT</div>
          <div data-testid="lobby-jackpot" className="font-serif text-3xl lg:text-4xl gold-text">{fmt(currency==="HUF"?jackpot.HUF:jackpot.EUR, currency)}</div>
        </div>
      </div>

      <h2 className="font-serif text-3xl mb-6 gold-text">{t(lang,"landing.games_title")}</h2>
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {games.map(g => (
          <Link to={g.to} key={g.to} data-testid={`lobby-game-${g.to.slice(1)}`} className="lux-card overflow-hidden group">
            <div className="aspect-[4/3] relative overflow-hidden">
              <img src={g.img} alt={g.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-700"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"/>
              <div className="absolute bottom-4 left-5 flex items-center gap-2 text-[#d4af37]">{g.icon}<span className="text-xs uppercase tracking-widest">{g.desc}</span></div>
            </div>
            <div className="p-5"><h3 className="font-serif text-xl">{g.name}</h3></div>
          </Link>
        ))}
      </div>

      <Link to="/instant" data-testid="lobby-instant-cta" className="lux-card overflow-hidden group relative block mb-12">
        <div className="relative bg-gradient-to-r from-[#3a0000] via-[#1a0a0a] to-[#000] p-8 flex items-center gap-6">
          <div className="w-16 h-16 rounded-full bg-[#d4af37]/20 flex items-center justify-center border border-[#d4af37]/40">
            <Zap className="text-[#d4af37]" size={32}/>
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.4em] text-[#d4af37]">New · Betmatch style</div>
            <h3 className="font-serif text-3xl mt-1">Instant Games</h3>
            <div className="text-white/60 text-sm mt-1">Crash · Mines · Plinko · Dice · Limbo · Dragon Tower</div>
          </div>
          <div className="text-[#d4af37] text-2xl">→</div>
        </div>
      </Link>

      <h2 className="font-serif text-2xl mb-4">{t(lang,"nav.history")}</h2>
      <div className="lux-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-white/50 text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Game</th><th className="text-right p-3">Bet</th><th className="text-right p-3">Payout</th><th className="text-right p-3">Net</th><th className="text-right p-3">When</th></tr>
          </thead>
          <tbody>
            {history.length===0 && <tr><td colSpan={5} className="p-6 text-center text-white/40">—</td></tr>}
            {history.slice(0,10).map((h,i)=>(
              <tr key={i} className="border-t border-white/5">
                <td className="p-3 capitalize">{h.game}</td>
                <td className="p-3 text-right">{fmt(h.bet, h.currency)}</td>
                <td className="p-3 text-right">{fmt(h.payout, h.currency)}</td>
                <td className={`p-3 text-right ${h.payout-h.bet>=0?"text-emerald-400":"text-red-400"}`}>{fmt(h.payout-h.bet, h.currency)}</td>
                <td className="p-3 text-right text-white/40 text-xs">{new Date(h.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
